import { Box, styled, Tooltip } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import {
  BadgeTypographyProps,
  EContractState,
  SearchField,
  SearchFieldOption,
  SearchFieldProps,
} from "@synota-io/synota-shared-ui";
import { DashboardContractState, useContracts } from "../../api/hooks/useContracts";
import { IServiceChargeContract } from "../../api/hooks/useServiceChargeContracts";

export type DashboardContractSearchOption =
  | (IServiceChargeContract & {
      label: string;
      value: string;
    })
  | null;

type Props = {
  onContractOpen?: (
    event: React.MouseEvent<Element, MouseEvent>,
    option: SearchFieldOption,
  ) => void;
  includeLinkedUsers?: boolean;
  displayInactive?: boolean;
  activeStates?: DashboardContractState[];
};

export type ContractSearchFieldProps = Omit<SearchFieldProps, "options"> & Props;

const DEFAULT_ACTIVE_STATES = [
  EContractState.Active,
  EContractState.Finishing,
  EContractState.Completed,
];

const CONTRACT_STATE_COLOR_MAP: Record<EContractState, BadgeTypographyProps["status"]> = {
  Active: "success",
  Finishing: "success",
  Completed: "info",
  Unsigned: "warning",
  Upcoming: "info",
};

const CONTRACT_STATE_ORDER: EContractState[] = [
  EContractState.Active,
  EContractState.Finishing,
  EContractState.Completed,
  EContractState.Upcoming,
  EContractState.Unsigned,
];

const ContractLink = ({
  option,
  onContractOpen,
}: {
  option: SearchFieldOption;
  onContractOpen: NonNullable<Props["onContractOpen"]>;
}) => (
  <Tooltip title="Go to Contract" placement="left">
    <OpenInNew role="button" fontSize="small" onClick={(e) => onContractOpen(e, option)} />
  </Tooltip>
);

export const DashboardContractSearchField = ({
  onContractOpen,
  displayInactive,
  activeStates = DEFAULT_ACTIVE_STATES,
  ...props
}: ContractSearchFieldProps) => {
  const { contracts: unfilteredContracts, isLoading } = useContracts();

  const contracts = (unfilteredContracts || [])
    .filter(
      (contract) =>
        displayInactive ||
        (contract.contract_state && activeStates.includes(contract.contract_state)),
    )
    .map((contract) => ({
      ...contract,
      contract_state:
        contract.contract_state === EContractState.Finishing
          ? EContractState.Active
          : contract.contract_state,
      label: contract.contract_display_name,
      value: contract.shared_uuid,
    }))
    .sort(
      (a, b) =>
        CONTRACT_STATE_ORDER.findIndex((s) => s === a.contract_state) -
        CONTRACT_STATE_ORDER.findIndex((s) => s === b.contract_state),
    );

  return (
    <SearchField
      label="Search Contract"
      {...props}
      loading={isLoading}
      groupBy={(option) => option.contract_state}
      renderOption={(props, option) => {
        return (
          <ListItem {...props} key={option.id}>
            <span>{option.label}</span>
            {onContractOpen ? (
              <>
                <Box component="span" flexGrow={1} />
                <ContractLink onContractOpen={onContractOpen} option={option} />
              </>
            ) : null}
          </ListItem>
        );
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <GroupHeader status={CONTRACT_STATE_COLOR_MAP[params.group as EContractState]}>
            {params.group}
          </GroupHeader>
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
      options={contracts}
    />
  );
};

const GroupHeader = styled("div")<{ status: BadgeTypographyProps["status"] }>(
  ({ theme, status = "primary" }) => ({
    position: "sticky",
    top: "-8px",
    padding: "4px 10px",
    color: theme.palette[status].main,
    backgroundColor: theme.palette.background.default,
  }),
);

const ListItem = styled("li")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "start",
  paddingRight: 0,
  "& .MuiSvgIcon-root": {
    color: theme.palette.text.secondary,
  },
  "& .MuiSvgIcon-root:hover": {
    color: theme.palette.primary.main,
  },
}));

const GroupItems = styled("ul")({
  padding: 0,
});
